"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Building2,
  ChevronDown,
  Crown,
  FileSpreadsheet,
  Image as ImageIcon,
  Loader2,
  Maximize2,
  Minimize2,
  Minus,
  Plus,
  Search,
  TrendingUp,
  TriangleAlert,
  Users,
  X,
} from "lucide-react";

import { Input } from "@/app/_module/components/ui/input";
import { Skeleton } from "@/app/_module/components/ui/skeleton";
import { useBranding } from "@/app/_module/store/contexts/brandingContext";
import { useAgencyHierarchy } from "../../hooks/use-agency-hierarchy";
import type { HierarchyTreeNode } from "../../services/agency-hierarchy";
import HierarchyLivePanel from "./HierarchyLivePanel";
import { analyseHierarchy, type NodeFlags } from "../../services/hierarchy-insights";
import { useSalePulse } from "../../hooks/use-sale-pulse";
import { useToast } from "@/app/_module/hooks/use-toast";
import HierarchyCompare from "./HierarchyCompare";
import { exportHierarchyCsv, exportHierarchyPng } from "../../services/hierarchy-export";
import {
  useAllLiveCalls,
  callsForAgency,
  liveCountsByAgency,
} from "../../hooks/use-hierarchy-live-calls";

/**
 * Agency Hierarchy — a real top-down org chart, not an indented list.
 *
 * Data: `GET /agencies/:id/sub-agency-tree` returns one flat row per agency (recursive
 * CTE) with agent count, owner and 30-day production. Nesting + roll-ups happen in
 * `buildHierarchyTree`.
 *
 * WHY A DARK CANVAS INSIDE A LIGHT DASHBOARD: the chart is a visualisation surface, not
 * another data table. Giving it its own dark ground separates "explore the org" from the
 * white cards around it, and lets the connector lines and role accents carry real signal
 * instead of competing with page chrome.
 *
 * CONNECTOR GEOMETRY — built from explicit divs rather than pseudo-elements so the bus
 * segments are inspectable and can't collapse at odd widths:
 *
 *        ┌──────────┐
 *        │  parent  │
 *        └────┬─────┘        stem  (h-6 w-px)
 *      ┌──────┴──────┐       bus   (first: left-1/2→right-0, last: left-0→right-1/2)
 *      │             │       drop  (h-6 w-px per child)
 *   ┌──┴──┐       ┌──┴──┐
 *   │child│       │child│
 *
 * Each node's PERSONAL is its own premium; HIERARCHY is itself plus every descendant;
 * VOLUME is that hierarchy figure as a share of the whole org — so the root always reads
 * 100% and a branch tells you instantly how much of the book it carries.
 */

const money = (n: number) => {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return `$${Math.round(n)}`;
};

type Role = "OWNER" | "MANAGER" | "AGENCY" | "ADMIN" | "AGENT";

/** Role drives the card's accent — the one place colour carries meaning here. */
const ROLE_STYLE: Record<Role, { ring: string; badge: string; dot: string; glow: string }> = {
  OWNER: {
    ring: "ring-amber-400/70",
    badge: "bg-amber-400/10 text-amber-300 ring-amber-400/30",
    dot: "bg-amber-400",
    glow: "shadow-[0_0_28px_-6px_rgba(251,191,36,0.45)]",
  },
  MANAGER: {
    ring: "ring-violet-400/50",
    badge: "bg-violet-400/10 text-violet-300 ring-violet-400/30",
    dot: "bg-violet-400",
    glow: "shadow-[0_0_24px_-8px_rgba(167,139,250,0.4)]",
  },
  AGENCY: {
    ring: "ring-slate-600/60",
    badge: "bg-slate-400/10 text-slate-300 ring-slate-400/20",
    dot: "bg-slate-400",
    glow: "",
  },
  // People-chart only. An AGENT is not a lesser AGENCY — giving them their own
  // label stops a rank being implied where none exists.
  ADMIN: {
    ring: "ring-sky-500/50",
    badge: "bg-sky-400/10 text-sky-300 ring-sky-400/20",
    dot: "bg-sky-400",
    glow: "",
  },
  AGENT: {
    ring: "ring-slate-600/60",
    badge: "bg-slate-400/10 text-slate-300 ring-slate-400/20",
    dot: "bg-slate-400",
    glow: "",
  },
};

const roleOf = (node: HierarchyTreeNode): Role => {
  // Person nodes carry their real agency_users role. Agency nodes have no `role` at
  // all, and keep the original depth-based rule. Without this branch every person in
  // the agents view rendered as OWNER, because buildPeopleTree() builds them at
  // depth 0 and depth 0 used to mean "root = owner" (caught on dev: Damian Wayne,
  // Dick Grayson and co. are agents, yet all wore the crown).
  const personRole = (node as any).role as string | undefined;
  if (personRole) {
    const r = personRole.toLowerCase();
    if (r === "owner" || r === "agency_owner") return "OWNER";
    if (r === "admin" || r === "agency_admin") return "ADMIN";
    return "AGENT";
  }
  return node.depth === 0 ? "OWNER" : node.children.length > 0 ? "MANAGER" : "AGENCY";
};

/**
 * BRAND-AWARE ACCENT.
 *
 * `BrandingProvider` already writes `--primary` / `--secondary` onto :root for
 * white-labelled tenants (brandingContext.tsx:275). Reading those here means the chart
 * inherits an agency's colour automatically — no per-tenant config, and InsuraCentral's
 * own palette is just the default value of the same variables.
 *
 * Falls back to the app's indigo when a tenant hasn't set a colour, so the chart never
 * renders with an unstyled accent.
 */
const BRAND = {
  // ⚠️ `--primary` holds a COMPLETE colour value (globals.css sets
  // `oklch(0.623 0.214 259.815)`, and BrandingProvider writes a normalised colour for
  // white-label tenants) — NOT the bare HSL channels some Tailwind setups use. Wrapping
  // it in `hsl(...)` produces invalid CSS and the accent silently disappears.
  // Use it directly, and derive translucent variants with color-mix so it works
  // regardless of the colour space the tenant's brand colour arrives in.
  accent: "var(--primary)",
  accentSoft: "color-mix(in oklab, var(--primary) 18%, transparent)",
  accentLine: "color-mix(in oklab, var(--primary) 55%, transparent)",
};

function NodeCard({
  node,
  share,
  collapsed,
  onToggle,
  dimmed,
  onSelect,
  selected,
  liveCount,
  highlighted,
  faded,
  onHover,
  flags,
  pulsing,
  query,
  uplineName,
  variant = "agency",
}: {
  node: HierarchyTreeNode;
  share: number;
  collapsed: boolean;
  onToggle: () => void;
  dimmed: boolean;
  onSelect: (shiftKey: boolean) => void;
  selected: boolean;
  liveCount: number;
  highlighted: boolean;
  faded: boolean;
  onHover: (id: string | null) => void;
  flags?: NodeFlags;
  pulsing?: boolean;
  /** Active search text, so a matched agent can be highlighted in the roster. */
  query?: string;
  /** Owner of the parent agency — this agency's direct upline. Null at the root. */
  uplineName?: string | null;
  /** 'person' swaps the money row for role / agency / team size. */
  variant?: "agency" | "person";
}) {
  const [rosterOpen, setRosterOpen] = useState(false);
  const role = roleOf(node);
  const style = ROLE_STYLE[role];
  const hasChildren = node.children.length > 0;

  return (
    <div
      role="button"
      tabIndex={0}
      onMouseEnter={() => onHover(node.id)}
      onMouseLeave={() => onHover(null)}
      onFocus={() => onHover(node.id)}
      onBlur={() => onHover(null)}
      onClick={(e) => onSelect(e.shiftKey)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          // Shift+Enter mirrors shift-click so compare is reachable from the keyboard.
          onSelect(e.shiftKey);
        }
      }}
      aria-label={`Open live activity for ${node.name}. Shift-click to compare branches.`}
      title="Click for live activity · Shift-click to compare two branches"
      className={`relative w-[264px] cursor-pointer rounded-xl bg-[#111528] px-4 py-3 ring-1 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 ${style.ring} ${style.glow} ${
        dimmed || faded ? "opacity-30" : "opacity-100"
      } ${selected ? "ring-2 ring-white/70" : ""} ${
        pulsing ? "hierarchy-sale-pulse" : ""
      } hover:-translate-y-0.5 hover:ring-2`}
      style={highlighted ? { boxShadow: `0 0 0 1px ${BRAND.accent}` } : undefined}
    >
      {/* Sale landed here (or somewhere beneath) since the last refresh. */}
      {pulsing && (
        <span className="pointer-events-none absolute -right-1.5 -top-1.5 flex h-5 items-center gap-0.5 rounded-full bg-emerald-500 px-1.5 text-[9px] font-bold text-white shadow-lg">
          <TrendingUp className="h-2.5 w-2.5" />
          SALE
        </span>
      )}

      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {role === "OWNER" ? (
            <Crown className="h-3.5 w-3.5 shrink-0 text-amber-400" />
          ) : (
            <Building2 className="h-3.5 w-3.5 shrink-0 text-slate-400" />
          )}
          <span className="truncate text-sm font-semibold text-slate-50" title={node.name}>
            {node.name}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {/* Live-call pulse: shows which branches are ACTIVE right now, so you can spot
              where the floor is working without opening every node. */}
          {liveCount > 0 && (
            <span
              className="flex items-center gap-1 rounded-full bg-emerald-400/10 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-300 ring-1 ring-inset ring-emerald-400/30"
              title={`${liveCount} call${liveCount === 1 ? "" : "s"} in progress`}
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
              </span>
              {liveCount}
            </span>
          )}
          <span
            className={`flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold tracking-wide ring-1 ring-inset ${style.badge}`}
          >
            <span className={`h-1 w-1 rounded-full ${style.dot}`} />
            {role}
          </span>
        </div>
      </div>

      <div className="mt-1 truncate text-[11px] text-slate-500" title={node.ownerName ?? ""}>
        {node.ownerName ?? "No owner assigned"}
      </div>

      {/* Risk flags — the "where am I losing money" layer. Only rendered when something
          is actually wrong, so a healthy card stays clean. */}
      {(flags?.deadWeight ||
        flags?.underPerforming ||
        flags?.concentrationRisk ||
        flags?.dormantRoster) && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {flags?.deadWeight && (
            <span
              className="rounded px-1 py-0.5 text-[9px] font-semibold bg-rose-500/15 text-rose-300 ring-1 ring-inset ring-rose-500/30"
              title={`${node.agentCount} agents, zero production this period`}
            >
              NO PRODUCTION
            </span>
          )}
          {flags?.underPerforming && (
            <span
              className="rounded px-1 py-0.5 text-[9px] font-semibold bg-amber-500/15 text-amber-300 ring-1 ring-inset ring-amber-500/30"
              title="Under half the org's production per agent"
            >
              BELOW AVG
            </span>
          )}
          {flags?.dormantRoster && (
            <span
              className="rounded px-1 py-0.5 text-[9px] font-semibold bg-violet-500/15 text-violet-300 ring-1 ring-inset ring-violet-500/30"
              title={`${node.staleAgents} of ${node.agentCount} agents have not logged in for 30+ days`}
            >
              {node.staleAgents}/{node.agentCount} DORMANT
            </span>
          )}
          {flags?.concentrationRisk && (
            <span
              className="rounded px-1 py-0.5 text-[9px] font-semibold bg-sky-500/15 text-sky-300 ring-1 ring-inset ring-sky-500/30"
              title="Carries over half of all production"
            >
              KEY BRANCH
            </span>
          )}
        </div>
      )}

      {variant === "person" ? (
        /* A person has no production of their own in this payload, so this row carries
           identity and span of control instead. Never money — see buildPeopleTree(). */
        <div className="mt-3 grid grid-cols-2 gap-x-3">
          <div className="min-w-0">
            <div className="truncate text-[9px] font-medium uppercase tracking-wide leading-none text-slate-500">
              Agency
            </div>
            <div className="mt-1 truncate text-[12px] font-semibold text-slate-100" title={node.ownerName ?? undefined}>
              {node.ownerName ?? "—"}
            </div>
          </div>
          <div className="min-w-0">
            {/* "Downline", not "Team" — it pairs with the "Upline:" line directly
                below, and the card already has an Agency column, so reusing that
                word here would just name the same thing twice. */}
            <div className="truncate text-[9px] font-medium uppercase tracking-wide leading-none text-slate-500">
              Downline
            </div>
            <div className="mt-1 truncate text-[12px] font-semibold tabular-nums" style={{ color: BRAND.accent }}>
              {node.children.length
                ? `${node.totalAgents - 1} under`
                : <span className="text-slate-500">no downline</span>}
            </div>
          </div>
        </div>
      ) : (
        <>
      <div className="mt-3 grid grid-cols-3 gap-x-3">
        <div>
          <div className="truncate text-[9px] font-medium uppercase tracking-wide leading-none text-slate-500">
            Sold
          </div>
          <div
            className="mt-1 truncate text-[13px] font-semibold tabular-nums text-slate-100"
            title="Premium on leads marked Sold & Approved, rolled up through this branch"
          >
            {money(node.totalSold)}
          </div>
        </div>
        <div>
          <div className="truncate text-[9px] font-medium uppercase tracking-wide leading-none text-slate-500">
            Production
          </div>
          <div
            className="mt-1 truncate text-[13px] font-semibold tabular-nums text-slate-100"
            title="Sold & Approved plus Underwriting — everything in flight"
          >
            {money(node.totalProduction)}
          </div>
        </div>
        <div>
          <div className="truncate text-[9px] font-medium uppercase tracking-wide leading-none text-slate-500">
            Volume
          </div>
          <div
            className="mt-1 truncate text-[13px] font-semibold tabular-nums"
            style={{ color: BRAND.accent }}
          >
            {share.toFixed(1)}%
          </div>
        </div>
      </div>

      {/* Share rail — the Volume % as a bar, so relative size reads without arithmetic.
          A branch carrying a third of the book LOOKS like a third at a glance. */}
      <div className="mt-2.5 h-1 overflow-hidden rounded-full bg-white/5">
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{
            width: `${Math.min(100, Math.max(share, share > 0 ? 2 : 0))}%`,
            background: `linear-gradient(90deg, ${BRAND.accent}, ${BRAND.accentLine})`,
          }}
        />
      </div>
        </>
      )}

      {uplineName && (
        <div className="mt-1 truncate text-[10px] text-slate-500" title={`Reports to ${uplineName}`}>
          Upline: <span className="text-slate-300">{uplineName}</span>
        </div>
      )}

      <div className="mt-2 flex items-center justify-between border-t border-white/5 pt-2">
        <span className="text-[10px] text-slate-500">
          {/* Clickable so the roster can be opened in place. The tab previously
              showed a headcount with no way to see who was in it. */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setRosterOpen((v) => !v); }}
            disabled={!node.agents?.length}
            aria-expanded={rosterOpen}
            className={node.agents?.length ? "underline-offset-2 hover:underline" : undefined}
            title={node.agents?.length ? "Show this agency's roster" : undefined}
          >
            {variant === "person" ? (
              // Their downline, not a roster — and silent when they have none, since
              // the Team column already says "no downline".
              node.totalAgents > 1 ? (
                <>
                  <span className="font-semibold text-slate-300">{node.totalAgents - 1}</span> in downline
                </>
              ) : null
            ) : (
              <>
                <span className="font-semibold text-slate-300">{node.totalAgents}</span>{" "}
                agent{node.totalAgents === 1 ? "" : "s"}
              </>
            )}
          </button>
          {node.descendantAgencies > 0 && (
            <>
              {" · "}
              <span className="font-semibold text-slate-300">{node.descendantAgencies}</span>{" "}
              sub{node.descendantAgencies === 1 ? "" : "s"}
            </>
          )}
        </span>
        {hasChildren && (
          <button
            type="button"
            onClick={(e) => {
              // The whole card is clickable, so without this a collapse/expand also
              // selects the node and swaps the side panel out from under the user.
              e.stopPropagation();
              onToggle();
            }}
            aria-expanded={!collapsed}
            aria-label={collapsed ? `Expand ${node.name}` : `Collapse ${node.name}`}
            className="flex h-5 w-5 items-center justify-center rounded text-slate-500 transition-colors hover:bg-white/10 hover:text-slate-200"
          >
            <ChevronDown
              className={`h-3.5 w-3.5 transition-transform duration-200 ${collapsed ? "-rotate-90" : ""}`}
            />
          </button>
        )}
      </div>

      {/* The roster itself. Owners first, then agents; whoever matches the current
          search is highlighted so a hit is findable inside a 22-person agency
          instead of just lighting the card. */}
      {rosterOpen && !!node.agents?.length && (
        <ul
          className="mt-2 space-y-1 border-t border-white/5 pt-2"
          onClick={(e) => e.stopPropagation()}
        >
          {node.agents.map((a) => {
            const q = (query ?? "").trim().toLowerCase();
            const hit =
              !!q &&
              ((a.name ?? "").toLowerCase().includes(q) ||
                (a.email ?? "").toLowerCase().includes(q));
            const isOwner = ["owner", "agency_owner"].includes(String(a.role));
            return (
              <li
                key={a.userId}
                className={`flex items-center justify-between gap-2 rounded px-1.5 py-0.5 text-[10px] ${
                  hit ? "bg-amber-400/15 text-amber-200" : "text-slate-400"
                }`}
                title={a.email || undefined}
              >
                <span className="truncate">{a.name}</span>
                {isOwner && (
                  <span className="shrink-0 rounded bg-white/10 px-1 text-[9px] uppercase tracking-wide text-slate-300">
                    owner
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * Connector weight = how much production flows down that branch.
 *
 * This is the thing a static org chart can't tell you: with uniform lines, a branch
 * holding 60% of the book looks identical to one holding 2%. Scaling stroke width and
 * brightness by the child's share of its parent makes the shape of the business visible
 * in the chart itself — you can see where the money goes without reading a number.
 */
function branchWeight(child: HierarchyTreeNode, parentTotal: number) {
  const share = parentTotal > 0 ? child.totalProduction / parentTotal : 0;
  return {
    // 1px minimum so an empty branch is still visibly connected, never orphaned-looking.
    width: `${(1 + share * 3).toFixed(2)}px`,
    opacity: 0.35 + share * 0.65,
  };
}

function TreeNode({
  node,
  rootTotal,
  collapsed,
  onToggle,
  matchedIds,
  onSelect,
  selectedId,
  liveCounts,
  highlightedIds,
  onHover,
  flagsById,
  pulsingIds,
  query,
  uplineName,
  variant = "agency",
}: {
  node: HierarchyTreeNode;
  rootTotal: number;
  collapsed: Set<string>;
  onToggle: (id: string) => void;
  matchedIds: Set<string> | null;
  onSelect: (node: HierarchyTreeNode, shiftKey: boolean) => void;
  selectedId: string | null;
  liveCounts: Map<string, number>;
  highlightedIds: Set<string> | null;
  onHover: (id: string | null) => void;
  flagsById: Map<string, NodeFlags>;
  pulsingIds: Set<string>;
  /** Live search text — passed down so a matched agent can be highlighted. */
  query?: string;
  /** Owner of the agency above this one. */
  uplineName?: string | null;
  variant?: "agency" | "person";
}) {
  const isCollapsed = collapsed.has(node.id);
  const kids = node.children;
  const showKids = kids.length > 0 && !isCollapsed;
  const share = rootTotal > 0 ? (node.totalProduction / rootTotal) * 100 : 0;
  const dimmed = !!matchedIds && !matchedIds.has(node.id);

  return (
    <div className="flex flex-col items-center">
      <NodeCard
        variant={variant}
        query={query}
        uplineName={uplineName}
        node={node}
        share={share}
        collapsed={isCollapsed}
        onToggle={() => onToggle(node.id)}
        dimmed={dimmed}
        onSelect={(shiftKey) => onSelect(node, shiftKey)}
        selected={selectedId === node.id}
        liveCount={liveCounts.get(node.id) ?? 0}
        highlighted={!!highlightedIds?.has(node.id)}
        faded={!!highlightedIds && !highlightedIds.has(node.id)}
        onHover={onHover}
        flags={flagsById.get(node.id)}
        pulsing={pulsingIds.has(node.id)}
      />

      {kids.length > 0 && (
        <div
          className={`bg-slate-600 transition-all ${showKids ? "h-6" : "h-3 opacity-40"}`}
          style={{ width: "2px" }}
        />
      )}

      {showKids && (
        <div className="flex items-start">
          {kids.map((child, i) => (
            <div key={child.id} className="relative flex flex-col items-center px-3">
              {/* horizontal bus — half-width at the ends so it stops at the outer drops */}
              {kids.length > 1 && (
                <div
                  className={`absolute top-0 h-px bg-slate-700 ${
                    i === 0
                      ? "left-1/2 right-0"
                      : i === kids.length - 1
                        ? "left-0 right-1/2"
                        : "left-0 right-0"
                  }`}
                />
              )}
              <div
                className="h-6"
                style={{
                  width: branchWeight(child, node.totalProduction).width,
                  opacity: branchWeight(child, node.totalProduction).opacity,
                  backgroundColor: BRAND.accent,
                }}
              />
              <TreeNode
                variant={variant}
                query={query}
                uplineName={variant === "person" ? node.name : node.ownerName}
                node={child}
                rootTotal={rootTotal}
                collapsed={collapsed}
                onToggle={onToggle}
                matchedIds={matchedIds}
                onSelect={onSelect}
                selectedId={selectedId}
                liveCounts={liveCounts}
                highlightedIds={highlightedIds}
                onHover={onHover}
                flagsById={flagsById}
                pulsingIds={pulsingIds}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/**
 * AGENTS VIEW — the same org chart drawn over PEOPLE instead of agencies.
 *
 * The reporting line is the one the Agency Agents tab already uses and that the
 * business actually works by: an agent reports to the owner of the agency they
 * sit in, and that owner reports to the owner of the agency above. Ownership
 * cascades down in `agency_users`, so every ancestor owner also holds an owner
 * row on every descendant — `node.agents` already has those stripped out, which
 * is why the LOCAL owner is simply the owner found on the agency itself.
 *
 * An agency with no local owner (its only owners are inherited) is not a level
 * in the people chart: its members are lifted to the nearest owner above, which
 * is where they really report. That is why walk() returns a LIST, not a node.
 *
 * ⚠️ `totalProduction` on a person node carries HEADCOUNT, not money. The shared
 * renderer sizes connectors and the share bar from that field, so in this view
 * branch thickness reads as team size. Person cards never print money — we have
 * no per-agent production here, and showing $0 would assert something false.
 */
type PersonRow = { userId: string; name: string; email: string; role: string };

const EMPTY_COUNTS = new Map<string, number>();
const EMPTY_FLAGS = new Map<string, NodeFlags>();

function buildPeopleTree(agencies: HierarchyTreeNode[]): HierarchyTreeNode[] {
  const isOwner = (r: string) => ["owner", "agency_owner"].includes((r || "").toLowerCase());

  /**
   * ⚠️ `node.agents` DOES include inherited owners, whatever the field comment says —
   * verified against the live endpoint, where Bruce Wayne came back as an owner of all
   * ten agencies in his tree because ownership cascades downward in `agency_users`.
   * Taking the first owner off the roster therefore made HIM the head of every agency,
   * every copy but one was deduped away, and the whole chart collapsed into a single
   * owner with a flat fan of agents beneath. So work out the LOCAL owner here: an owner
   * of this agency who does not already own something above it.
   */
  const inheritedOwners = new Map<string, Set<string>>();
  {
    const walkDown = (node: HierarchyTreeNode, fromAbove: Set<string>) => {
      inheritedOwners.set(node.id, fromAbove);
      const ownersHere = new Set(fromAbove);
      for (const a of (node.agents ?? []) as PersonRow[]) {
        if (isOwner(a.role)) ownersHere.add(a.userId);
      }
      for (const child of node.children) walkDown(child, ownersHere);
    };
    for (const root of agencies) walkDown(root, new Set());
  }
  const localOwnersOf = (agency: HierarchyTreeNode): PersonRow[] => {
    const inherited = inheritedOwners.get(agency.id) ?? new Set<string>();
    return ((agency.agents ?? []) as PersonRow[]).filter((a) => isOwner(a.role) && !inherited.has(a.userId));
  };

  const person = (
    p: PersonRow,
    agencyName: string,
    children: HierarchyTreeNode[],
  ): HierarchyTreeNode => {
    const headcount = children.reduce((n, c) => n + (c.totalAgents || 1), 0);
    return {
      id: `person:${p.userId}`,
      name: p.name || p.email || "Unnamed",
      parentAgencyId: null,
      depth: 0,
      // the card's secondary line: where this person sits
      ownerName: agencyName,
      ownerEmail: p.email ?? null,
      agents: [],
      agentCount: children.length,
      totalAgents: headcount + 1,
      staleAgents: 0,
      totalStaleAgents: 0,
      descendantAgencies: 0,
      soldPremium: 0,
      underwritingPremium: 0,
      production: 0,
      soldPolicies: 0,
      underwritingPolicies: 0,
      totalSold: 0,
      // headcount, NOT money — see the note above
      totalProduction: headcount,
      children,
      // carried for the card + search
      role: (p.role || "agent").toLowerCase(),
    } as unknown as HierarchyTreeNode;
  };

  const walk = (agency: HierarchyTreeNode): HierarchyTreeNode[] => {
    const roster: PersonRow[] = (agency.agents ?? []) as PersonRow[];
    // An agency can have CO-OWNERS (Imperium: Rafael De Julio + Tobe; TEAM MIGHTY:
    // Ali Sharif + Esa Mohamed). Taking only the first would silently drop the other
    // from the chart entirely — it did, until this was caught. The first heads the
    // agency; the rest hang off them as peers so everyone stays visible exactly once.
    const owners = localOwnersOf(agency);
    const inherited = inheritedOwners.get(agency.id) ?? new Set<string>();
    // an inherited owner is not a member of this agency — they sit at their own level
    const members = roster.filter((a) => !isOwner(a.role) && !inherited.has(a.userId));
    const below = agency.children.flatMap(walk);
    const memberNodes = members.map((m) => person(m, agency.name, []));
    const coOwnerNodes = owners.slice(1).map((o) => person(o, agency.name, []));
    const kids = [...coOwnerNodes, ...memberNodes, ...below].sort((a, b) => {
      // biggest teams first, then alphabetical — same instinct as the agency view
      if (b.totalProduction !== a.totalProduction) return b.totalProduction - a.totalProduction;
      return a.name.localeCompare(b.name);
    });
    return owners.length ? [person(owners[0], agency.name, kids)] : kids;
  };

  /**
   * One card per PERSON, exactly like the Agency Agents tab. Someone who belongs to
   * two agencies in the tree (Daniel Gaci sits in Duse Agency AND Assurity Enterprise
   * Group) would otherwise render twice — duplicate React keys and a chart that
   * double-counts the org. Keep the shallowest placement, which is also the most
   * senior, and lift anyone beneath a dropped copy so no one disappears with it.
   */
  const dedupe = (roots: HierarchyTreeNode[]): HierarchyTreeNode[] => {
    // Where someone appears BOTH as an owner and as an agent (they run a sub-agency
    // while sitting on another roster), the owner placement wins — the same rule the
    // Agency Agents tab collapses by, so the two screens never disagree about who
    // someone is. Shallowest still decides between two placements of equal rank.
    const ownsSomewhere = new Set<string>();
    (function scan(nodes: HierarchyTreeNode[]) {
      for (const n of nodes) {
        if (["owner", "agency_owner"].includes(String((n as any).role ?? "").toLowerCase())) {
          ownsSomewhere.add(n.id);
        }
        scan(n.children);
      }
    })(roots);

    const seen = new Set<string>();
    const pass = (nodes: HierarchyTreeNode[]): HierarchyTreeNode[] => {
      const kept: HierarchyTreeNode[] = [];
      for (const n of nodes) {
        const isOwnerCopy = ["owner", "agency_owner"].includes(String((n as any).role ?? "").toLowerCase());
        // skip a non-owner copy of someone who owns an agency elsewhere in the tree
        if (seen.has(n.id) || (!isOwnerCopy && ownsSomewhere.has(n.id))) {
          kept.push(...pass(n.children)); // drop the copy, keep its people
          continue;
        }
        seen.add(n.id);
        kept.push(n);
      }
      // breadth-first: settle this level before descending, so the shallowest wins
      for (const n of kept) n.children = pass(n.children);
      return kept;
    };
    return pass(roots);
  };

  /** Headcounts are counted during walk(), i.e. BEFORE dedupe drops a second copy of
   *  someone — so roll them up again on the final shape or every ancestor reads one
   *  person too many. (Moe Ray showed 191 under him when it is 190.) */
  const rollUp = (n: HierarchyTreeNode): number => {
    const below = n.children.reduce((sum, c) => sum + rollUp(c), 0);
    (n as any).agentCount = n.children.length;
    (n as any).totalAgents = below + 1;
    (n as any).totalProduction = below; // headcount, drives connector weight only
    return below + 1;
  };

  const roots = dedupe(agencies.flatMap(walk));
  roots.forEach(rollUp);
  return roots;
}

/** Depth-first flatten for the CSV export — parents before their children, so the file
 *  reads top-down the same way the chart does. */
function flattenTree(nodes: HierarchyTreeNode[], out: HierarchyTreeNode[] = []) {
  for (const n of nodes) {
    out.push(n);
    flattenTree(n.children, out);
  }
  return out;
}

export default function AgencyHierarchyTab({
  agencyId,
  startDate,
  endDate,
  period,
}: {
  agencyId?: string;
  /** Comes from the dashboard's own date filter so this tab never disagrees with the
      Premium Sold boxes above it, including custom ranges and reset. */
  startDate?: string;
  endDate?: string;
  /** Preset window ('6-months' | '12-months' | 'year-to-date'); resolved server-side. */
  period?: string;
}) {
  const { tree, isLoading, isError, refetch } = useAgencyHierarchy(agencyId, {
    startDate,
    endDate,
    period,
  });

  // Shift-click two branches to compare them side by side. IDs, not node objects, for
  // the same staleness reason as `selectedId`.
  const [compareIds, setCompareIds] = useState<string[]>([]);
  // The pan/zoom TRANSFORM wrapper, not the scroll viewport — exporting the viewport
  // would capture whatever happens to be panned into view at the current zoom.
  const chartRef = useRef<HTMLDivElement | null>(null);
  const [exporting, setExporting] = useState(false);
  const [query, setQuery] = useState("");
  // Attention is a LENS on the tree, not a list beside it: switching it on fades every
  // branch that is fine, so what is left lit IS the problem. Findings then read as a
  // compact overlay inside the canvas instead of a full-width card above it.
  const [attentionOn, setAttentionOn] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  // Opens TALL by default. The short canvas meant hitting Expand on every visit — an org
  // chart is only useful when you can see the org. The toggle now shrinks it, not grows it.
  const [expandedView, setExpandedView] = useState(true);
  // Stored by ID, never as the node OBJECT. The tree is rebuilt on every refetch — and
  // the sale pulse forces one — so holding the object would keep the open panel showing
  // pre-sale figures, and would keep it open on a node that no longer exists.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // ONE live-call feed for the whole tab: the chart cards read counts from it and the
  // side panel filters it. Two hooks each opening the same channel is what broke
  // presence after the Supabase upgrade — don't repeat it here.
  const allCalls = useAllLiveCalls();
  // Hovering a node lights up its chain of command to the root. In a deep org, working
  // out "who does this roll up to" otherwise means tracing lines by eye.
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const liveCounts = useMemo(() => liveCountsByAgency(allCalls), [allCalls]);

  // Deterministic back-office analysis — dead weight, under-performers, concentration.
  const { flagsById, insights } = useMemo(() => analyseHierarchy(tree), [tree]);
  const criticalCount = insights.filter((i) => i.severity === "critical").length;
  const warningCount = insights.filter((i) => i.severity === "warning").length;

  // ── Live sale pulse ────────────────────────────────────────────────────────────
  // A sale lands → refetch the tree → light up every node whose production actually
  // moved. Diffing the numbers is what makes the ripple travel UP the tree for free:
  // the closer's agency and every ancestor above it all gain production in the same
  // refetch, so the whole upline chain lights in one pass. It also means a sale in
  // someone else's org lights nothing, with no agency filter to get wrong.
  const { toast } = useToast();
  const [pulsingIds, setPulsingIds] = useState<Set<string>>(new Set());
  const prevProduction = useRef<Map<string, number> | null>(null);
  const pulseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useSalePulse(agencyId, (sale) => {
    toast({
      title: "💰 New sale",
      description: [
        sale.agentName ?? "An agent",
        "closed a deal",
        sale.carrier ? `with ${sale.carrier}` : "",
        sale.policyAmountDollars
          ? `— $${sale.policyAmountDollars.toLocaleString()}/yr`
          : "",
      ]
        .filter(Boolean)
        .join(" "),
    });
    void refetch();
  });

  useEffect(() => {
    const next = new Map<string, number>();
    const walk = (nodes: HierarchyTreeNode[]) => {
      for (const n of nodes) {
        next.set(n.id, n.totalProduction);
        walk(n.children);
      }
    };
    walk(tree);

    const prev = prevProduction.current;
    prevProduction.current = next;
    // First load is a baseline, not a change — otherwise every card flashes on mount.
    if (!prev) return;

    const moved = new Set<string>();
    next.forEach((value, id) => {
      const before = prev.get(id);
      if (before !== undefined && value > before) moved.add(id);
    });
    if (!moved.size) return;

    setPulsingIds(moved);
    if (pulseTimer.current) clearTimeout(pulseTimer.current);
    pulseTimer.current = setTimeout(() => setPulsingIds(new Set()), 4_000);
  }, [tree]);

  useEffect(
    () => () => {
      if (pulseTimer.current) clearTimeout(pulseTimer.current);
    },
    [],
  );

  // Re-derived from the CURRENT tree every render, so a refetch (including the one the
  // sale pulse triggers) flows straight into the open panel. Resolves to null if the
  // agency has left the tree, which closes the panel rather than stranding it.
  const selected = useMemo(() => {
    if (!selectedId) return null;
    const find = (nodes: HierarchyTreeNode[]): HierarchyTreeNode | null => {
      for (const n of nodes) {
        if (n.id === selectedId) return n;
        const hit = find(n.children);
        if (hit) return hit;
      }
      return null;
    };
    return find(tree);
  }, [selectedId, tree]);

  const compare = useMemo(() => {
    const byId = new Map<string, HierarchyTreeNode>();
    const walk = (nodes: HierarchyTreeNode[]) => {
      for (const n of nodes) {
        byId.set(n.id, n);
        walk(n.children);
      }
    };
    walk(tree);
    // filter(Boolean) drops a branch that has left the tree since it was picked.
    return compareIds.map((id) => byId.get(id)).filter(Boolean) as HierarchyTreeNode[];
  }, [compareIds, tree]);

  const windowLabel = startDate
    ? "custom range"
    : period === "12-months"
      ? "last 12 months"
      : period === "year-to-date"
        ? "year to date"
        : "last 6 months";

  const handleSelect = useCallback((node: HierarchyTreeNode, shiftKey: boolean) => {
    if (!shiftKey) {
      setSelectedId(node.id);
      return;
    }
    // Shift-click builds a comparison pair. Clicking an already-picked branch removes it
    // so a mis-pick is one click to undo, and the third pick replaces the older of the
    // two rather than silently doing nothing.
    setCompareIds((prev) => {
      if (prev.includes(node.id)) return prev.filter((id) => id !== node.id);
      return prev.length < 2 ? [...prev, node.id] : [prev[1], node.id];
    });
  }, []);

  // Pan + zoom. A 40-agency org is far wider than any viewport, and scrollbars alone
  // make it feel like a document rather than a canvas. Transform-based so it stays
  // smooth (no layout/reflow per frame) and the connector geometry scales with it.
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = React.useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  /**
   * Drag vs click.
   *
   * ⚠️ The first version captured the pointer on pointerDOWN, which meant every press
   * became a drag and the card's click never landed — you had to click repeatedly to
   * open a node. Now a press only becomes a drag once the pointer has actually MOVED
   * past a small threshold; below that it stays a plain click and the card handles it.
   */
  const DRAG_THRESHOLD_PX = 4;

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      // Controls inside the canvas keep their own behaviour.
      if ((e.target as HTMLElement).closest("button, input")) return;
      // Record the origin but DON'T capture or set dragging yet — that's what killed
      // the click.
      dragRef.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
    },
    [pan],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const dx = e.clientX - d.x;
      const dy = e.clientY - d.y;

      if (!dragging) {
        if (Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD_PX) return; // still a click
        setDragging(true);
        try {
          (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        } catch {
          /* capture unsupported — panning still works, just without capture */
        }
      }
      setPan({ x: d.panX + dx, y: d.panY + dy });
    },
    [dragging],
  );

  const endDrag = useCallback((e: React.PointerEvent) => {
    dragRef.current = null;
    setDragging(false);
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* nothing captured — expected on a plain click */
    }
  }, []);

  const resetView = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  const onToggle = useCallback((id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Agencies or the people inside them — same chart, same geometry, same search.
  const [viewMode, setViewMode] = useState<"agencies" | "agents">("agencies");
  const peopleTree = useMemo(() => buildPeopleTree(tree), [tree]);
  const displayTree = viewMode === "agents" ? peopleTree : tree;
  const isPeople = viewMode === "agents";

  /** Owner of the agency directly above the open node — its upline. Null at the root. */
  const selectedUplineName = useMemo(() => {
    if (!selectedId) return null;
    let found: string | null = null;
    const walk = (n: HierarchyTreeNode, parentOwner: string | null): boolean => {
      if (n.id === selectedId) { found = parentOwner; return true; }
      return n.children.some((c) => walk(c, isPeople ? n.name : n.ownerName));
    };
    displayTree.some((r) => walk(r, null));
    return found;
  }, [selectedId, displayTree, isPeople]);

  const rootTotal = useMemo(
    () => displayTree.reduce((n, r) => n + r.totalProduction, 0),
    [displayTree],
  );

  /** id -> [self, parent, grandparent, ...] so a hover can highlight the whole line. */
  const ancestryById = useMemo(() => {
    const map = new Map<string, string[]>();
    const walk = (node: HierarchyTreeNode, chain: string[]) => {
      const own = [node.id, ...chain];
      map.set(node.id, own);
      node.children.forEach((c) => walk(c, own));
    };
    displayTree.forEach((n) => walk(n, []));
    return map;
  }, [displayTree]);

  const highlightedIds = useMemo(
    () => (hoveredId ? new Set(ancestryById.get(hoveredId) ?? []) : null),
    [hoveredId, ancestryById],
  );

  // Always fully expanded on load — an org chart that opens collapsed makes an owner
  // click before seeing anything. Collapse stays available per node.
  const expandAll = useCallback(() => setCollapsed(new Set()), []);

  /** Flagged agencies plus their ancestors, so a lit leaf keeps a visible path to the root. */
  const attentionIds = useMemo(() => {
    if (!insights.length) return null;
    const flagged = new Set(insights.map((i) => i.agencyId));
    const keep = new Set<string>();
    const walk = (node: HierarchyTreeNode, ancestors: string[]) => {
      if (flagged.has(node.id)) {
        keep.add(node.id);
        ancestors.forEach((a) => keep.add(a));
      }
      node.children.forEach((c) => walk(c, [...ancestors, node.id]));
    };
    tree.forEach((n) => walk(n, []));
    return keep;
  }, [tree, insights]);

  /** Search highlights rather than filters — removing nodes would break the geometry. */
  const searchIds = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    const keep = new Set<string>();
    const walk = (node: HierarchyTreeNode, ancestors: string[]): boolean => {
      // Match the agency, its owner, OR anyone on its roster. Agent search was the
      // gap: the node carried only a headcount, so typing an agent's name lit up
      // nothing even though they were plainly in the tree.
      const self =
        node.name.toLowerCase().includes(q) ||
        (node.ownerName ?? "").toLowerCase().includes(q) ||
        (node.agents ?? []).some(
          (a) =>
            (a.name ?? "").toLowerCase().includes(q) ||
            (a.email ?? "").toLowerCase().includes(q),
        );
      const child = node.children.map((c) => walk(c, [...ancestors, node.id])).some(Boolean);
      if (self || child) {
        keep.add(node.id);
        ancestors.forEach((a) => keep.add(a));
        return true;
      }
      return false;
    };
    displayTree.forEach((n) => walk(n, []));
    return keep;
  }, [displayTree, query]);

  // Search and the attention lens compose: with both on, only branches that satisfy
  // BOTH stay lit.
  const matchedIds = useMemo(() => {
    const attention = attentionOn ? attentionIds : null;
    if (!searchIds && !attention) return null;
    if (!searchIds) return attention;
    if (!attention) return searchIds;
    return new Set([...searchIds].filter((id) => attention.has(id)));
  }, [searchIds, attentionIds, attentionOn]);

  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-[68px] w-full rounded-lg" />
          ))}
        </div>
        <Skeleton className="h-[420px] w-full rounded-xl" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-lg border border-dashed p-8 text-center">
        <p className="text-sm text-muted-foreground">
          Couldn&apos;t load the hierarchy. Refresh to try again.
        </p>
      </div>
    );
  }

  if (!tree.length) {
    return (
      <div className="rounded-lg border border-dashed p-10 text-center">
        <Building2 className="mx-auto mb-3 h-8 w-8 text-slate-300" />
        <p className="font-medium text-slate-900">No agencies to show yet</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Create a sub-agency and your organisation chart will build itself here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* ⚠️ NO summary tiles here on purpose.
          Performance Overview at the top of this page already reports Total Production,
          Total Issued Sold & Approved and Agent Count for the same org and the same
          period — restating them under the chart just gave the numbers a second place to
          disagree (the headcount tile managed 5, 27 and 37 in one sitting before it was
          pulled). The chart itself is the content; per-agency figures live on the cards,
          where they mean one specific thing. */}
      {compare.length === 2 && (
        <HierarchyCompare
          a={compare[0]}
          b={compare[1]}
          accent={BRAND.accent}
          onClose={() => setCompareIds([])}
        />
      )}

      {compare.length === 1 && (
        <div className="rounded-lg border border-dashed bg-slate-50 px-4 py-2 text-xs text-slate-600">
          <span className="font-semibold">{compare[0].name}</span> selected — shift-click
          another agency to compare.
          <button
            type="button"
            onClick={() => setCompareIds([])}
            className="ml-2 font-medium underline underline-offset-2"
          >
            Cancel
          </button>
        </div>
      )}


      {/* Chart canvas */}
      <div className="overflow-hidden rounded-xl bg-[#0A0D1C] ring-1 ring-slate-800">
        <div className="org-toolbar flex items-center justify-between gap-3 border-b border-slate-800 px-4 py-3">
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <span className="flex gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-slate-700" />
              <span className="h-2.5 w-2.5 rounded-full bg-slate-700" />
              <span className="h-2.5 w-2.5 rounded-full bg-slate-700" />
            </span>
            <span className="ml-1 hidden sm:inline">
              Hierarchy tree · production rollup
            </span>
          </div>

          <div className="flex items-center gap-2">
            {/* Agencies ⇄ Agents. The agent chart is the same org drawn over people:
                an agent sits under their agency's owner, that owner under the owner
                above. Attention flags are agency analytics, so they switch off here. */}
            <div className="flex items-center rounded-md bg-slate-800 p-0.5" role="group" aria-label="Chart mode">
              {(["agencies", "agents"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => { setViewMode(m); setSelectedId(null); setCompareIds([]); setCollapsed(new Set()); }}
                  aria-pressed={viewMode === m}
                  title={m === "agents" ? "Show every agent in the hierarchy" : "Show the agency structure"}
                  className={`flex items-center gap-1.5 rounded px-2 py-1 text-[11px] font-semibold capitalize transition-colors ${
                    viewMode === m ? "bg-slate-600 text-white" : "text-slate-400 hover:text-slate-200"
                  }`}
                >
                  {m === "agencies" ? <Building2 className="h-3.5 w-3.5" /> : <Users className="h-3.5 w-3.5" />}
                  {m}
                </button>
              ))}
            </div>
            {/* Attention toggle — counts live in the chip so the state of the org reads
                at a glance without spending a whole card on it. */}
            {insights.length > 0 && !isPeople && (
              <button
                type="button"
                onClick={() => setAttentionOn((v) => !v)}
                aria-pressed={attentionOn}
                title={
                  attentionOn
                    ? "Showing only branches that need attention"
                    : "Dim everything that's healthy"
                }
                className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-semibold transition-colors ${
                  attentionOn
                    ? "bg-rose-500/20 text-rose-200 ring-1 ring-inset ring-rose-500/40"
                    : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                }`}
              >
                <TriangleAlert className="h-3.5 w-3.5" />
                {criticalCount > 0 && <span className="text-rose-300">{criticalCount}</span>}
                {criticalCount > 0 && warningCount > 0 && (
                  <span className="text-slate-500">·</span>
                )}
                {warningCount > 0 && <span className="text-amber-300">{warningCount}</span>}
              </button>
            )}
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-500" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Find agency or owner…"
                aria-label="Search the hierarchy"
                className="h-8 w-40 border-slate-700 bg-slate-900/60 pl-8 text-xs text-slate-200 placeholder:text-slate-500 focus-visible:ring-slate-600 sm:w-56"
              />
            </div>
            <span
              className="rounded-md bg-slate-800 px-2 py-1 text-[10px] font-semibold text-slate-300"
              title="Matches the dashboard's date filter"
            >
              {startDate
                ? "CUSTOM"
                : period === "12-months"
                  ? "12M"
                  : period === "year-to-date"
                    ? "YTD"
                    : "6M"}
            </span>
            {collapsed.size > 0 && (
              <button
                type="button"
                onClick={expandAll}
                className="rounded-md bg-slate-800/60 px-2 py-1 text-[10px] font-semibold text-slate-300 transition-colors hover:bg-slate-700 hover:text-white"
              >
                Expand all
              </button>
            )}
            <div className="flex items-center gap-0.5 rounded-md bg-slate-800/60 p-0.5">
              <button
                type="button"
                onClick={() => setZoom((z) => Math.max(0.4, +(z - 0.15).toFixed(2)))}
                aria-label="Zoom out"
                className="flex h-6 w-6 items-center justify-center rounded text-slate-400 transition-colors hover:bg-slate-700 hover:text-slate-100"
              >
                <Minus className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={resetView}
                aria-label="Reset view"
                className="px-1.5 text-[10px] font-semibold tabular-nums text-slate-300 transition-colors hover:text-white"
              >
                {Math.round(zoom * 100)}%
              </button>
              <button
                type="button"
                onClick={() => setZoom((z) => Math.min(1.6, +(z + 0.15).toFixed(2)))}
                aria-label="Zoom in"
                className="flex h-6 w-6 items-center justify-center rounded text-slate-400 transition-colors hover:bg-slate-700 hover:text-slate-100"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
            <button
              type="button"
              onClick={() => {
                const stamp = windowLabel.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
                exportHierarchyCsv(flattenTree(tree), `hierarchy-${stamp}.csv`);
              }}
              aria-label="Export CSV"
              title="Download as CSV"
              className="flex h-7 w-7 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-800 hover:text-slate-200"
            >
              <FileSpreadsheet className="h-3.5 w-3.5" />
            </button>

            <button
              type="button"
              disabled={exporting}
              onClick={async () => {
                if (!chartRef.current) return;
                setExporting(true);
                try {
                  const stamp = windowLabel.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
                  await exportHierarchyPng(chartRef.current, `hierarchy-${stamp}.png`);
                } catch {
                  toast({
                    title: "Couldn't export the chart",
                    description: "Try the CSV export instead.",
                    variant: "destructive",
                  });
                } finally {
                  setExporting(false);
                }
              }}
              aria-label="Export PNG"
              title="Download as image"
              className="flex h-7 w-7 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-800 hover:text-slate-200 disabled:opacity-40"
            >
              {exporting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <ImageIcon className="h-3.5 w-3.5" />
              )}
            </button>

            <button
              type="button"
              onClick={() => setExpandedView((v) => !v)}
              aria-label={expandedView ? "Shrink chart" : "Expand chart"}
              className="flex h-7 w-7 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-800 hover:text-slate-200"
            >
              {expandedView ? (
                <Minimize2 className="h-3.5 w-3.5" />
              ) : (
                <Maximize2 className="h-3.5 w-3.5" />
              )}
            </button>
          </div>
        </div>

        {/* Canvas + live panel sit side by side so selecting a node never hides the chart */}
        <div className={`relative flex ${expandedView ? "h-[78vh]" : "h-[520px]"}`}>
        <div
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          data-no-ptr
          className={`relative h-full min-h-0 flex-1 overflow-hidden px-8 py-10 ${
            dragging ? "cursor-grabbing" : "cursor-grab"
          }`}
          style={{
            // touch-action none: without it iOS cancels the pointermove pan
            // and scrolls the page instead — the chart was undraggable on
            // touch. Zoom stays on the +/- buttons.
            touchAction: "none",
            backgroundImage:
              "linear-gradient(rgba(148,163,184,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.05) 1px, transparent 1px)",
            backgroundSize: "36px 36px",
            // Grid drifts WITH the content so panning reads as moving across a surface
            // rather than sliding cards over a fixed backdrop.
            backgroundPosition: `${pan.x}px ${pan.y}px`,
          }}
        >
          <div
            ref={chartRef}
            className="flex min-w-max justify-center gap-12"
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transformOrigin: "top center",
              transition: dragging ? "none" : "transform 180ms ease-out",
            }}
          >
            {displayTree.map((node) => (
              <TreeNode
                variant={isPeople ? "person" : "agency"}
                query={query}
                key={node.id}
                node={node}
                rootTotal={rootTotal}
                collapsed={collapsed}
                onToggle={onToggle}
                matchedIds={matchedIds}
                onSelect={handleSelect}
                selectedId={selectedId}
                // live calls and back-office flags are keyed by AGENCY id — they mean
                // nothing on a person node, so they stay empty rather than mislead.
                liveCounts={isPeople ? EMPTY_COUNTS : liveCounts}
                highlightedIds={highlightedIds}
                onHover={setHoveredId}
                flagsById={isPeople ? EMPTY_FLAGS : flagsById}
                pulsingIds={pulsingIds}
              />
            ))}
          </div>
          </div>

          {/* Findings ride INSIDE the canvas as a glass overlay, so the tree stays the
              subject and the list is context — rather than a full-width white card that
              pushed the chart down the page. */}
          {attentionOn && insights.length > 0 && (
            <div className="pointer-events-none absolute left-4 top-4 z-20 w-[300px]">
              <div className="pointer-events-auto overflow-hidden rounded-xl border border-slate-700/70 bg-slate-950/85 shadow-2xl backdrop-blur-md">
                <div className="flex items-center justify-between border-b border-slate-800 px-3 py-2">
                  <span className="flex items-baseline gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-300">
                    Needs attention
                    {/* Count in the header so a scrolled-off list doesn't look complete. */}
                    <span className="font-normal normal-case tracking-normal text-slate-500">
                      {insights.length}
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={() => setAttentionOn(false)}
                    aria-label="Close findings"
                    className="text-slate-500 transition-colors hover:text-slate-200"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className={`overflow-y-auto ${expandedView ? "max-h-[46vh]" : "max-h-[280px]"}`}>
                  {insights.map((i) => (
                    <button
                      key={i.id}
                      type="button"
                      onClick={() => setSelectedId(i.agencyId)}
                      onMouseEnter={() => setHoveredId(i.agencyId)}
                      onMouseLeave={() => setHoveredId(null)}
                      className="flex w-full items-start gap-2 border-b border-slate-800/70 px-3 py-2 text-left transition-colors last:border-b-0 hover:bg-slate-800/60"
                    >
                      <span
                        className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${
                          i.severity === "critical"
                            ? "bg-rose-400"
                            : i.severity === "warning"
                              ? "bg-amber-400"
                              : "bg-slate-500"
                        }`}
                      />
                      <span className="min-w-0 flex-1 text-[11px] leading-relaxed text-slate-300">
                        {i.message}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {selected && !compare.length && (
            <HierarchyLivePanel
              node={selected}
              uplineName={selectedUplineName}
              accent={BRAND.accent}
              calls={callsForAgency(allCalls, selected.id)}
              onClose={() => setSelectedId(null)}
            />
          )}
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        <strong className="font-medium text-slate-700">Sold</strong> is premium on
        Sold &amp; Approved leads. <strong className="font-medium text-slate-700">Production</strong>{" "}
        adds Underwriting — everything in flight.{" "}
        <strong className="font-medium text-slate-700">Volume</strong> is the branch&apos;s
        share of total production. All figures roll up through sub-agencies and follow the
        date filter above.
      </p>
    </div>
  );
}
